import type { AxiosResponse } from 'axios';
import { AdvancedAnalysisResponse, ChatRequestParameters, ChatResponse } from './experimentalApiTypes';
import {
  Api as GeneratedApi,
  HttpClient,
  ContentType,
  CreatePartFinderReportRequest,
  CreatePartFinderReportResponse,
  ListPartFinderReportsResponse,
  GetPartFinderReportResponse,
  GetFolderFiltersResponse,
  GetReportGroupFiltersResponse,
} from './generatedApi';
import { FeatureFlagName } from '@/utils/featureFlags';

export interface ViewerFileResponse {
  data: ArrayBuffer;
}

export type ServerSegmentationResult = {
  regionMap: string;
  width: number;
  height: number;
  regions: { id: number; pixelCount: number }[];
};

export interface GetMetadataFieldsWithDataResponse {
  metadataFieldNames: string[];
}

type GeneratedAssetsType = GeneratedApi<unknown>['assets'];

interface EnhancedAssetsType extends GeneratedAssetsType {
  getAdvancedAnalysis(id: string, tenantId: string): Promise<AxiosResponse<AdvancedAnalysisResponse>>;
  chat(
    id: string,
    tenantId: string,
    parameters: ChatRequestParameters,
    onChunk?: (chunk: string) => void
  ): Promise<AxiosResponse<ChatResponse>>;
  getSupplementalAssets(
    tenantId: string,
    assetId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<{ supplementalAssets: unknown[]; pageData: unknown }>>;
  deleteSupplementalAssets(
    tenantId: string,
    assetId: string,
    data: { supplementalAssetIds: string[] }
  ): Promise<AxiosResponse<void>>;
  putCriticalGeometry(
    tenantId: string,
    assetId: string,
    data: { releaseUnitVector?: [number, number, number] }
  ): Promise<AxiosResponse<{ ok: true; analysisAssetId: string; artifactPath: string }>>;
  putManifestCriticalGeometry(tenantId: string, assetId: string): Promise<AxiosResponse<{ analysisAssetId: string }>>;
  segmentImage(
    tenantId: string,
    file: File,
    pointsPerSide?: number,
    sensitivity?: number
  ): Promise<AxiosResponse<ServerSegmentationResult>>;
}

export type FeatureFlags = {
  [key in FeatureFlagName]: boolean;
};

interface EnhancedTenantsType {
  getFeatureFlags(tenantId: string): Promise<AxiosResponse<FeatureFlags>>;
  createPartFinderReport(
    tenantId: string,
    data: CreatePartFinderReportRequest
  ): Promise<AxiosResponse<CreatePartFinderReportResponse>>;
  listPartFinderReports(
    tenantId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<ListPartFinderReportsResponse>>;
  getPartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderReportResponse>>;
  deletePartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<void>>;
  getRootFilters(tenantId: string): Promise<AxiosResponse<GetFolderFiltersResponse>>;
  getFolderFilters(tenantId: string, folderId: string): Promise<AxiosResponse<GetFolderFiltersResponse>>;
  getRootMetadataFieldsWithData(tenantId: string): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>>;
  getFolderMetadataFieldsWithData(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>>;
  getReportGroupFilters(
    tenantId: string,
    reportId: string,
    groupId: string
  ): Promise<AxiosResponse<GetReportGroupFiltersResponse>>;
}
export class Api extends GeneratedApi<unknown> {
  declare assets: EnhancedAssetsType;
  declare tenants: EnhancedTenantsType;

  constructor(http: HttpClient) {
    super(http);
    Object.assign(this.assets, {
      getAdvancedAnalysis: this.getAdvancedAnalysis.bind(this),
      chat: this.chat.bind(this),
      getSupplementalAssets: this.getSupplementalAssets.bind(this),
      deleteSupplementalAssets: this.deleteSupplementalAssets.bind(this),
      putCriticalGeometry: this.putCriticalGeometry.bind(this),
      putManifestCriticalGeometry: this.putManifestCriticalGeometry.bind(this),
      segmentImage: this.segmentImage.bind(this),
    });

    if (!this.tenants) {
      this.tenants = {} as EnhancedTenantsType;
    }
    Object.assign(this.tenants, {
      getFeatureFlags: this.getFeatureFlags.bind(this),
      createPartFinderReport: this.createPartFinderReport.bind(this),
      listPartFinderReports: this.listPartFinderReports.bind(this),
      getPartFinderReport: this.getPartFinderReport.bind(this),
      deletePartFinderReport: this.deletePartFinderReport.bind(this),
      getRootFilters: this.getRootFilters.bind(this),
      getFolderFilters: this.getFolderFilters.bind(this),
      getRootMetadataFieldsWithData: this.getRootMetadataFieldsWithData.bind(this),
      getFolderMetadataFieldsWithData: this.getFolderMetadataFieldsWithData.bind(this),
      getReportGroupFilters: this.getReportGroupFilters.bind(this),
    });
  }

  private async getAdvancedAnalysis(id: string, tenantId: string): Promise<AxiosResponse<AdvancedAnalysisResponse>> {
    return this.http.request<AdvancedAnalysisResponse>({
      path: `/tenants/${tenantId}/assets/${id}/advanced-analysis`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async chat(
    id: string,
    tenantId: string,
    parameters: ChatRequestParameters,
    onChunk?: (chunk: string) => void
  ): Promise<AxiosResponse<ChatResponse>> {
    return this.http.request<ChatResponse>({
      path: `/tenants/${tenantId}/assets/${id}/advanced-analysis/chat`,
      method: 'POST',
      secure: true,
      format: 'json',
      body: parameters,
      onDownloadProgress: progressEvent => {
        if (onChunk && progressEvent.event.target) {
          const chunk = progressEvent.event.target.responseText;
          onChunk(chunk);
        }
      },
    });
  }

  private async getFeatureFlags(tenantId: string): Promise<AxiosResponse<FeatureFlags>> {
    return this.http.request<FeatureFlags>({
      path: `/tenants/${tenantId}/feature-flags`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async createPartFinderReport(
    tenantId: string,
    data: CreatePartFinderReportRequest
  ): Promise<AxiosResponse<CreatePartFinderReportResponse>> {
    return this.http.request<CreatePartFinderReportResponse>({
      path: `/tenant/${tenantId}/part-finder-reports`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async listPartFinderReports(
    tenantId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<ListPartFinderReportsResponse>> {
    return this.http.request<ListPartFinderReportsResponse>({
      path: `/tenant/${tenantId}/part-finder-reports`,
      method: 'GET',
      query: {
        ...(page !== undefined && { page }),
        ...(perPage !== undefined && { perPage }),
      },
      secure: true,
      format: 'json',
    });
  }

  private async getPartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderReportResponse>> {
    return this.http.request<GetPartFinderReportResponse>({
      path: `/tenant/${tenantId}/part-finder-reports/${id}`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async deletePartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenant/${tenantId}/part-finder-reports/${id}`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async getSupplementalAssets(
    tenantId: string,
    assetId: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<AxiosResponse<{ supplementalAssets: unknown[]; pageData: unknown }>> {
    return this.http.request<{ supplementalAssets: unknown[]; pageData: unknown }>({
      path: `/tenants/${tenantId}/assets/${assetId}/supplemental-assets`,
      method: 'GET',
      secure: true,
      format: 'json',
      query: { page, perPage },
    });
  }

  private async deleteSupplementalAssets(
    tenantId: string,
    assetId: string,
    data: { supplementalAssetIds: string[] }
  ): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/assets/${assetId}/supplemental-assets`,
      method: 'DELETE',
      secure: true,
      format: 'json',
      body: data,
    });
  }

  private async putCriticalGeometry(
    tenantId: string,
    assetId: string,
    data: { releaseUnitVector?: [number, number, number] }
  ): Promise<AxiosResponse<{ ok: true; analysisAssetId: string; artifactPath: string }>> {
    return this.http.request<{ ok: true; analysisAssetId: string; artifactPath: string }>({
      path: `/tenants/${tenantId}/assets/${assetId}/critical-geometry`,
      method: 'PUT',
      secure: true,
      format: 'json',
      body: data,
    });
  }

  private async putManifestCriticalGeometry(
    tenantId: string,
    assetId: string
  ): Promise<AxiosResponse<{ analysisAssetId: string }>> {
    return this.http.request<{ analysisAssetId: string }>({
      path: `/tenants/${tenantId}/assets/${assetId}/manifest-critical-geometry`,
      method: 'PUT',
      secure: true,
      format: 'json',
    });
  }

  private async getRootFilters(tenantId: string): Promise<AxiosResponse<GetFolderFiltersResponse>> {
    return this.http.request<GetFolderFiltersResponse>({
      path: `/tenants/${tenantId}/folders/root/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderFilters(tenantId: string, folderId: string): Promise<AxiosResponse<GetFolderFiltersResponse>> {
    return this.http.request<GetFolderFiltersResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootMetadataFieldsWithData(
    tenantId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>> {
    return this.http.request<GetMetadataFieldsWithDataResponse>({
      path: `/tenants/${tenantId}/folders/root/metadata-fields-with-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderMetadataFieldsWithData(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>> {
    return this.http.request<GetMetadataFieldsWithDataResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/metadata-fields-with-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getReportGroupFilters(
    tenantId: string,
    reportId: string,
    groupId: string
  ): Promise<AxiosResponse<GetReportGroupFiltersResponse>> {
    return this.http.request<GetReportGroupFiltersResponse>({
      path: `/tenant/${tenantId}/reports/${reportId}/groups/${groupId}/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async segmentImage(
    tenantId: string,
    file: File,
    pointsPerSide?: number,
    sensitivity?: number
  ): Promise<AxiosResponse<ServerSegmentationResult>> {
    return this.http.request<ServerSegmentationResult>({
      path: `/tenants/${tenantId}/assets/segment`,
      method: 'POST',
      body: {
        file,
        ...(pointsPerSide !== undefined && { pointsPerSide: pointsPerSide.toString() }),
        ...(sensitivity !== undefined && { sensitivity: sensitivity.toString() }),
      },
      secure: true,
      type: ContentType.FormData,
      format: 'json',
    });
  }

  public async recordUsageEvent(data: {
    name: string;
    url: string;
    title?: string;
    referrer?: string;
    data?: Record<string, unknown>;
  }): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/usage/record`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }
}
